
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Client } from "@/components/clients/types";

export const useClients = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const clientsQuery = useQuery({
    queryKey: ['clients', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        console.log('No authenticated user, returning empty clients array');
        return [];
      }

      console.log('Fetching clients for user:', session.user.id);

      // Query with explicit user_id filter for strict user isolation
      // RLS policies will also enforce this at database level
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', session.user.id) // Explicit user filtering
        .is('deleted_at', null) // Only get non-deleted clients
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }

      console.log('Successfully fetched clients:', data?.length || 0);
      return data as Client[];
    },
    enabled: !!session?.user?.id,
  });

  const createClientMutation = useMutation({
    mutationFn: async (clientData: Partial<Client>) => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('clients')
        .insert([{ 
          ...clientData, 
          user_id: session.user.id, // Ensure client belongs to current user
          name: clientData.name || ''
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create client');
      console.error('Error creating client:', error);
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: async ({ id, ...clientData }: Partial<Client> & { id: string }) => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('clients')
        .update({ ...clientData, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', session.user.id) // Ensure user can only update their own clients
        .is('deleted_at', null)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update client');
      console.error('Error updating client:', error);
    },
  });

  return {
    ...clientsQuery,
    createClient: createClientMutation.mutate,
    updateClient: updateClientMutation.mutate,
    isCreating: createClientMutation.isPending,
    isUpdating: updateClientMutation.isPending,
  };
};
